import {Trans} from '@lingui/macro';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {RepositionPopover} from '../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {Anchor} from '../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserTheme, useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DateTimeFormatterBase} from '../../../../srcCommon/formatters/dateTime/dateTimeFormatterBase';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {
  CalendarEventGridParameters,
  FocusedEventSourcesEnum,
} from '../../../store/panels/calendars/calendarsPanelState';
import {CalendarsViewTypesEnum} from '../../../store/panels/calendars/calendarsParameters';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {
  buildFullCalendarEventGradientBackground,
  FullCalendarEvent,
  makeFullCalendarEventId,
  renderFullCalendarEventContent,
} from './fullCalendarHelpers';

const minPopoverWidth = 200;
const popoverWidthMargin = 20;

/*
 * Props.
 */

interface MoreEventsPopoverProps {
  dayRect: DOMRectReadOnly;
  date: Date;
  fullCalendarEvents: ReadonlyArray<FullCalendarEvent>;
  localize: Localizer;
  timeFormat: TimeFormatsEnum;
  onRequestClose: Handler;
  onEventClick?: HandlerOf<CalendarEventGridParameters>;
  hasShow15MinuteEvents?: boolean;
}

/*
 * Styles.
 */

interface StyleProps {
  $maxHeight: number;
  $width: number;
}

const StyledPopoverWrapperDiv = styled.div<StyleProps>`
  background: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0px 2px 10px rgba(0, 0, 0, 0.2)`)};
  border-radius: 6px;
  display: grid;
  grid-template-rows: 40px 1fr;
  overflow: hidden;
  width: ${(p) => p.$width && `${p.$width}px`};
  max-height: ${(p) => p.$maxHeight && `${p.$maxHeight}px`};
`;

const StyledHeaderDiv = styled.div`
  height: 40px;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  position: relative;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: 5px;
  right: 8px;
`;

const StyledContentDiv = styled.div`
  padding: 12px 2px 12px 12px;
  overflow-x: hidden;
  overflow-y: scroll;
`;

const StyledEventDiv = styled.div`
  margin-bottom: 2px;
`;

/*
 * Component.
 */

export const MoreEventsPopover: FC<MoreEventsPopoverProps> = (props) => {
  // Calibrate the anchor to display the popover on top of the day grid element.
  const {dayRect} = props;
  const anchor: Anchor = useMemo(() => {
    const anchorRect = DOMRectReadOnly.fromRect({
      x: dayRect.x,
      y: dayRect.top - 10,
      width: dayRect.width,
      height: 0,
    });

    return () => anchorRect;
  }, [dayRect]);

  const {window} = useLayer();
  const theme = useUserTheme();
  const bodyRect = window.document.body.getBoundingClientRect();

  const dayCellWidth = props.dayRect.width + popoverWidthMargin;
  return (
    <RepositionPopover anchor={anchor} placement="bottom">
      <StyledPopoverWrapperDiv
        $maxHeight={bodyRect.height - props.dayRect.top}
        $width={dayCellWidth > minPopoverWidth ? dayCellWidth : minPopoverWidth}
      >
        <StyledHeaderDiv>
          <DateTimeFormatterBase
            value={props.date}
            format={{weekday: 'short', month: 'short', day: 'numeric'}}
          />
          <StyledIconButton
            iconName="close"
            onClick={props.onRequestClose}
            label={<Trans id="more-events-popover-close">Close</Trans>}
          />
        </StyledHeaderDiv>
        <StyledContentDiv>{renderCalendarWrapper(props, theme)}</StyledContentDiv>
      </StyledPopoverWrapperDiv>
    </RepositionPopover>
  );
};

function renderCalendarWrapper(props: MoreEventsPopoverProps, theme: UserTheme) {
  // Mock the fullcalendar grid container so that we can use a single definition of event css.
  return (
    <div className="fullcalendar-wrapper fc-wrapper-month-view">
      <div className="fc">
        <div className="fc-view-container">
          <div className="fc-view fc-dayGridMonth-view">{renderEvents(props, theme)}</div>
        </div>
      </div>
    </div>
  );
}

function renderEvents(props: MoreEventsPopoverProps, theme: UserTheme) {
  return props.fullCalendarEvents.map((e) => {
    if (!e.start || !e.end) {
      return null;
    }

    const eventDisplayInfo = {
      startDate: e.start,
      endDate: e.end,
      isAllDay: Boolean(e.allDay),
      summary: e.summary,
      durationMinutes: e.durationMinutes,
      accessRole: e.calendarVersionAccessRole,
      dotBackgroundColor: e.dotBackgroundColor,
      dotBorderColor: e.dotBorderColor,
      isDurationEditable: false,
      isPrivateEvent: e.isPrivateEvent,
    };

    const eventHtml = renderFullCalendarEventContent(
      eventDisplayInfo,
      CalendarsViewTypesEnum.MONTH,
      props.timeFormat,
      props.localize,
      props.hasShow15MinuteEvents,
    );

    const id = makeFullCalendarEventId(
      e.calendarEventVersionUid,
      e.calendarVersionId,
      e.instanceStartDate,
      true,
    );

    const onEventClick = () => {
      if (!props.onEventClick) {
        return;
      }

      props.onEventClick({
        calendarEventVersionUid: e.calendarEventVersionUid,
        calendarVersionId: e.calendarVersionId,
        instanceStartDate: e.instanceStartDate,
        source: FocusedEventSourcesEnum.MORE_EVENTS_POPOVER,
        overrides: {},
      });
    };

    const background =
      e.gradientColor && e.allDay
        ? buildFullCalendarEventGradientBackground(e.gradientColor, theme)
        : undefined;

    return (
      <StyledEventDiv
        id={id}
        key={id}
        className={`fc-event ${e.className} ${e.allDay ? 'fc-day-grid-event' : ''}`}
        style={{
          background,
          backgroundColor: e.backgroundColor,
          borderColor: e.borderColor,
          color: e.textColor,
        }}
        onClick={onEventClick}
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={{
          __html: eventHtml,
        }}
      />
    );
  });
}
